import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Alert,
  Platform,
  Image,
  Modal,
  ActivityIndicator,
} from 'react-native';
import EditAppointmentModal from '../components/EditAppointmentModal';
import EditOrderItemsModal from '../components/EditOrderItemsModal';
import DurationTrackingModal from '../components/DurationTrackingModal';
import RequestPaymentModal from '../components/RequestPaymentModal';
import AppointmentPaymentModal from '../components/AppointmentPaymentModal';
import CreateAppointmentModal from '../components/CreateAppointmentModal';
import BusinessOwnerBottomMenu from '../components/BusinessOwnerBottomMenu';
import { appointmentManager, Appointment } from '../utils/appointmentManager';
import { AuthManager } from '../utils/authManager';
import LiveQueueCard from '../components/LiveQueueCard';
import {
  getDateRangeForBusinessFilter,
  appointmentsToCSV,
  downloadCSV,
  groupAppointmentsIntoDateSections,
  type BusinessDateFilter,
} from '../utils/appointmentFilterUtils';
import { formatBookingPaymentMethod, isBookingPrepaid } from '../utils/appointmentPaymentDisplay';
import { showCenteredAlert } from '../utils/centeredAlert';
import {
  normalizeCustomerQueueVisibility,
  type CustomerQueueVisibility,
} from '../types/customerQueueVisibility';
import { AppointmentPrepaidBadge } from '../components/AppointmentPrepaidBadge';

interface Worker {
  id: string;
  name: string;
  phone: string;
  email: string;
  specialties: string;
}

interface AppointmentsScreenProps {
  onBack: () => void;
  username?: string;
  onTabPress?: (tab: 'dashboard' | 'appointments' | 'analytics') => void;
  onNavigateToSettings?: () => void;
  onLogout?: () => void;
  onNavigateToPackageSelection?: () => void;
}
declare const window: any;
declare const localStorage: any;

const FILTER_LABELS: Record<BusinessDateFilter, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  this_week: 'This week',
  archive: 'Archive',
};

const toLocalDateKey = (dateInput: string): string => {
  if (!dateInput) return '';
  const match = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return dateInput;
  const dt = new Date(dateInput);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DELETE_CONFIRM_MESSAGE =
  'This appointment will be permanently deleted and cannot be retrieved. Are you sure you want to delete?';

const AppointmentsScreen: React.FC<AppointmentsScreenProps> = ({ onBack, username: _username, onTabPress, onNavigateToSettings, onLogout, onNavigateToPackageSelection }) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [workers, _setWorkers] = useState<Worker[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [showEditAppointment, setShowEditAppointment] = useState(false);
  const [showEditOrderItems, setShowEditOrderItems] = useState(false);
  const [showDurationTracking, setShowDurationTracking] = useState(false);
  const [showRequestPayment, setShowRequestPayment] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showCreateAppointment, setShowCreateAppointment] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [businessOwnerId, setBusinessOwnerId] = useState<string>('');
  const [businessHours, setBusinessHours] = useState<any>(null);
  const [dateFilter, setDateFilter] = useState<BusinessDateFilter>('this_week');
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [queueRefreshing, setQueueRefreshing] = useState(false);
  const [lastQueueRefreshAt, setLastQueueRefreshAt] = useState<Date | null>(null);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [expandedAppointmentId, setExpandedAppointmentId] = useState<string | null>(null);
  const [customerQueueVisibility, setCustomerQueueVisibility] = useState<CustomerQueueVisibility>('off');
  const queueInsights = appointmentManager.computeQueueInsights(appointments, { businessOwnerId });
  const queueHealth = {
    total: appointments.length,
    inService: appointments.filter(a => a.queueStage === 'in_service').length,
    delayed: appointments.filter(a => (a.delayMinutes ?? 0) > 0).length,
    avgAhead: appointments.length
      ? Math.round(
          appointments.reduce((sum, apt) => sum + (queueInsights[apt.id]?.peopleAhead ?? 0), 0) /
            appointments.length
        )
      : 0,
  };

  // Load business owner data first
  useEffect(() => {
    loadBusinessOwnerData();
  }, []);

  // Load appointments by date range (Option B: only load selected range)
  const loadAppointmentsByFilter = useCallback(async (
    filter: BusinessDateFilter,
    options?: { showLoading?: boolean }
  ) => {
    if (!businessOwnerId) {
      setAppointments([]);
      return;
    }
    const shouldShowLoading = options?.showLoading ?? true;
    if (shouldShowLoading) {
      setLoadingFilter(true);
    }
    try {
      const { startDate, endDate } = getDateRangeForBusinessFilter(filter);
      const list = await appointmentManager.getAppointmentsByDateRange(businessOwnerId, startDate, endDate, {
        limit: filter === 'archive' ? 100 : 200,
      });
      setAppointments(list);
      setLastQueueRefreshAt(new Date());
    } catch (error) {
      console.error('Error loading appointments by filter:', error);
      setAppointments([]);
    } finally {
      if (shouldShowLoading) {
        setLoadingFilter(false);
      }
    }
  }, [businessOwnerId]);

  // Live subscription: appointments update the instant a booking is created or
  // changed in Firestore, from any device/session — a customer booking on the
  // widget from their own phone shows up here without the merchant refreshing.
  // Replaces the old same-tab window-event/localStorage-marker hack (which only
  // fired when the booking and dashboard shared a browser tab) and the 60s poll.
  useEffect(() => {
    if (!businessOwnerId) return;
    const { startDate, endDate } = getDateRangeForBusinessFilter(dateFilter);
    setLoadingFilter(true);
    const unsubscribe = appointmentManager.subscribeToAppointmentsByDateRange(
      businessOwnerId,
      startDate,
      endDate,
      (list) => {
        setAppointments(list);
        setLastQueueRefreshAt(new Date());
        setLoadingFilter(false);
      },
      (error) => {
        console.warn('Live appointments subscription failed, falling back to one-time load:', error);
        loadAppointmentsByFilter(dateFilter, { showLoading: true });
      },
      { limit: dateFilter === 'archive' ? 100 : 200 }
    );
    return () => unsubscribe();
  }, [businessOwnerId, dateFilter, loadAppointmentsByFilter]);

  const loadBusinessOwnerData = async () => {
    try {
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (businessOwner) {
        setBusinessOwnerId(businessOwner.id);
        if (businessOwner.businessHours) {
          setBusinessHours(businessOwner.businessHours);
        }
        setCustomerQueueVisibility(
          normalizeCustomerQueueVisibility((businessOwner as { customerQueueVisibility?: unknown }).customerQueueVisibility),
        );
        console.log('📅 AppointmentsScreen: Business owner ID:', businessOwner.id);
      }
    } catch (error) {
      console.error('Error loading business owner data:', error);
    }
  };

  const formatBusinessHours = (hours: any): string => {
    if (!hours) return 'Hours not set';
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const dayAbbrevs = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const openDays: string[] = [];
    
    daysOfWeek.forEach((day, index) => {
      if (hours[day]?.isOpen && hours[day]?.openTime && hours[day]?.closeTime) {
        openDays.push(`${dayAbbrevs[index]}: ${hours[day].openTime}-${hours[day].closeTime}`);
      }
    });
    
    return openDays.length > 0 ? openDays.join(' | ') : 'Closed';
  };


  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadAppointmentsByFilter(dateFilter, { showLoading: true });
    } catch (error) {
      console.error('Error refreshing appointments:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const onQueueRefresh = async () => {
    setQueueRefreshing(true);
    try {
      await loadAppointmentsByFilter(dateFilter, { showLoading: false });
    } catch (error) {
      console.error('Error refreshing live queue:', error);
    } finally {
      setQueueRefreshing(false);
    }
  };

  const handleFilterSelect = (filter: BusinessDateFilter) => {
    // The live subscription effect re-runs on dateFilter change and loads the
    // new range itself — no separate one-time fetch needed here.
    setDateFilter(filter);
  };

  const handleExportCurrent = () => {
    setShowExportDropdown(false);
    const csv = appointmentsToCSV(appointments, true);
    const name = `bookapt-appointments-${dateFilter}-${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csv, name);
    if (Platform.OS !== 'web') Alert.alert('Exported', `Saved ${appointments.length} appointments to ${name}`);
  };

  const handleExportArchive = async () => {
    setShowExportDropdown(false);
    if (!businessOwnerId) return;
    setLoadingFilter(true);
    try {
      const { startDate, endDate } = getDateRangeForBusinessFilter('archive');
      const archiveList = await appointmentManager.getAppointmentsByDateRange(businessOwnerId, startDate, endDate, { limit: 2000 });
      const csv = appointmentsToCSV(archiveList, true);
      const name = `bookapt-appointments-archive-${new Date().toISOString().split('T')[0]}.csv`;
      downloadCSV(csv, name);
      if (Platform.OS !== 'web') Alert.alert('Exported', `Saved ${archiveList.length} archive appointments to ${name}`);
    } catch (e) {
      console.error('Export archive failed:', e);
      Alert.alert('Error', 'Failed to export archive.');
    } finally {
      setLoadingFilter(false);
    }
  };

  const handleEditAppointment = (appointment: Appointment) => {
    console.log('📝 AppointmentsScreen: Edit appointment clicked for:', appointment.customerName);
    console.log('📝 AppointmentsScreen: Setting showEditAppointment to true');
    setSelectedAppointment(appointment);
    setShowEditAppointment(true);
  };

  const handleEditOrderItems = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setShowEditOrderItems(true);
  };

  const handleTrackDuration = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setShowDurationTracking(true);
  };

  const handleRequestPayment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setShowRequestPayment(true);
  };

  const handleProcessPayment = (appointment: Appointment) => {
    console.log('💳 AppointmentsScreen: Process payment clicked for:', appointment.customerName);
    console.log('💳 AppointmentsScreen: Setting showPayment to true');
    setSelectedAppointment(appointment);
    setShowPayment(true);
  };

  const handleCancelAppointment = async (appointment: Appointment) => {
    const confirmCancel = () => {
      if (Platform.OS === 'web') {
        if (window.confirm(`Are you sure you want to cancel the appointment for ${appointment.customerName}?`)) {
          performCancel();
        }
      } else {
        Alert.alert(
          'Cancel Appointment',
          `Are you sure you want to cancel the appointment for ${appointment.customerName}?`,
          [
            { text: 'No', style: 'cancel' },
            {
              text: 'Yes, Cancel',
              style: 'destructive',
              onPress: performCancel,
            },
          ]
        );
      }
    };

    const performCancel = async () => {
      try {
        console.log('🔄 Cancelling appointment:', appointment.id);
        await appointmentManager.updateAppointmentStatus(appointment.id, 'cancelled');
        console.log('✅ Appointment status updated to cancelled');
        
        // Reload appointments to reflect the change
        await loadAppointmentsByFilter(dateFilter);
        console.log('✅ Appointments reloaded');
        
        if (Platform.OS === 'web') {
          window.alert('Appointment Cancelled\n\nThe appointment has been cancelled successfully.');
        } else {
          Alert.alert('Appointment Cancelled', 'The appointment has been cancelled successfully.');
        }
      } catch (error) {
        console.error('❌ Error cancelling appointment:', error);
        if (Platform.OS === 'web') {
          window.alert('Error\n\nFailed to cancel appointment. Please try again.');
        } else {
          Alert.alert('Error', 'Failed to cancel appointment. Please try again.');
        }
      }
    };

    confirmCancel();
  };

  const handleDeleteAppointment = async (appointment: Appointment) => {
    const performDelete = async () => {
      try {
        console.log('🗑️ Deleting appointment:', appointment.id);
        setAppointments(prev => prev.filter(apt => apt.id !== appointment.id));
        setExpandedAppointmentId((cur) => (cur === appointment.id ? null : cur));
        await appointmentManager.deleteAppointment(appointment.id);
        await loadAppointmentsByFilter(dateFilter, { showLoading: false });
        showCenteredAlert('Deleted', 'The appointment has been permanently deleted.');
      } catch (error) {
        console.error('❌ Error deleting appointment:', error);
        await loadAppointmentsByFilter(dateFilter, { showLoading: false });
        showCenteredAlert('Error', 'Failed to delete appointment. Please try again.');
      }
    };

    showCenteredAlert('Delete Appointment', DELETE_CONFIRM_MESSAGE, [
      { text: 'Keep Appointment', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: performDelete },
    ]);
  };

  const handleCreateAppointment = () => {
    console.log('➕ AppointmentsScreen: Create appointment clicked');
    setShowCreateAppointment(true);
  };

  const handleSaveNewAppointment = async (appointmentData: Omit<Appointment, 'id' | 'createdAt'>) => {
    try {
      console.log('💾 AppointmentsScreen: Saving new appointment...');
      const dataWithOwner = { ...appointmentData, businessOwnerId };
      await appointmentManager.saveAppointment(dataWithOwner);
      setShowCreateAppointment(false);
      await loadAppointmentsByFilter(dateFilter);
      Alert.alert('Success', 'Appointment created successfully!');
    } catch (error) {
      console.error('❌ Error creating appointment:', error);
      Alert.alert('Error', 'Failed to create appointment. Please try again.');
    }
  };

  const handleSaveAppointment = async (updatedAppointment: Appointment) => {
    // Optimistic local update so the UI reflects the change immediately...
    setAppointments(prev =>
      prev.map(apt => apt.id === updatedAppointment.id ? updatedAppointment : apt)
    );
    setShowEditAppointment(false);
    setSelectedAppointment(null);

    // ...but this previously never actually persisted to Firestore, so a
    // reschedule would silently revert on the next data reload. EditAppointmentModal
    // maps its "notes" field onto specialInstructions since that's the real
    // field on Appointment.
    try {
      await appointmentManager.updateAppointmentSchedule(updatedAppointment.id, {
        appointmentDate: updatedAppointment.appointmentDate,
        appointmentTime: updatedAppointment.appointmentTime,
        specialInstructions: (updatedAppointment as any).notes ?? updatedAppointment.specialInstructions,
      });
    } catch (error) {
      console.error('Failed to persist rescheduled appointment:', error);
      Alert.alert('Error', 'Could not save the reschedule. Please try again.');
    }
  };

  const handleQueueAction = async (
    appointment: Appointment,
    action: 'checked_in' | 'in_service' | 'delay' | 'complete' | 'no_show'
  ) => {
    try {
      const nowIso = new Date().toISOString();
      if (action === 'checked_in') {
        await appointmentManager.updateAppointmentQueueFields(appointment.id, {
          queueStage: 'checked_in',
          checkedInAt: nowIso,
        });
      } else if (action === 'in_service') {
        await appointmentManager.updateAppointmentQueueFields(appointment.id, {
          queueStage: 'in_service',
          serviceStartedAt: nowIso,
        });
      } else if (action === 'delay') {
        await appointmentManager.updateAppointmentQueueFields(appointment.id, {
          delayMinutes: (appointment.delayMinutes ?? 0) + 10,
          delayReason: 'Provider running late',
        });
      } else if (action === 'complete') {
        await appointmentManager.updateAppointmentQueueFields(appointment.id, {
          queueStage: 'completed',
          serviceCompletedAt: nowIso,
        });
        await appointmentManager.updateAppointmentStatus(appointment.id, 'completed');
      } else if (action === 'no_show') {
        await appointmentManager.updateAppointmentQueueFields(appointment.id, {
          queueStage: 'no_show',
        });
        await appointmentManager.updateAppointmentStatus(appointment.id, 'cancelled');
      }
      await loadAppointmentsByFilter(dateFilter);
    } catch (error) {
      console.error('Queue action failed:', error);
      Alert.alert('Error', 'Failed to update queue status.');
    }
  };

  const handleSaveDuration = (appointmentId: string, duration: number) => {
    setAppointments(prev =>
      prev.map(apt => 
        apt.id === appointmentId ? { ...apt, actualDuration: duration } : apt
      )
    );
    setShowDurationTracking(false);
    setSelectedAppointment(null);
  };


  const handlePaymentSuccess = (updatedAppointment: Appointment) => {
    setShowPayment(false);
    setSelectedAppointment(null);

    setAppointments(prev =>
      prev.map(apt => (apt.id === updatedAppointment.id ? updatedAppointment : apt))
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return '#28a745';
      case 'completed': return '#007bff';
      case 'paid': return '#6f42c1';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Confirmed';
      case 'completed': return 'Completed';
      case 'paid': return 'Paid';
      case 'cancelled': return 'Cancelled';
      default: return 'Pending';
    }
  };

  const getLiveQueueState = (appointment: Appointment): 'show' | 'future' | 'hide' => {
    const todayKey = toLocalDateKey(new Date().toISOString());
    const aptDateKey = toLocalDateKey(appointment.appointmentDate);
    if (!aptDateKey || aptDateKey !== todayKey) {
      return aptDateKey > todayKey ? 'future' : 'hide';
    }
    if (appointment.status === 'completed' || appointment.status === 'cancelled') return 'hide';
    if (appointment.queueStage === 'completed' || appointment.queueStage === 'no_show') return 'hide';
    return 'show';
  };

  const appointmentDateSections = useMemo(
    () =>
      groupAppointmentsIntoDateSections(appointments, {
        descending: dateFilter === 'archive',
      }),
    [appointments, dateFilter]
  );

  useEffect(() => {
    setExpandedAppointmentId(null);
  }, [dateFilter]);

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerTitleRow}>
            <Image source={require('../assets/images/bookapt-logo.png')} style={styles.headerBookaptIcon} resizeMode="contain" />
            <Text style={styles.headerTitle}>Appointments</Text>
          </View>
          <View style={styles.headerButtons}>
            <TouchableOpacity 
              style={styles.refreshButton} 
              onPress={onRefresh}
              disabled={refreshing}
            >
              <Text style={styles.refreshButtonText}>
                {refreshing ? '🔄' : '↻'} {refreshing ? 'Refreshing...' : 'Refresh'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.backButton} onPress={onBack}>
              <Text style={styles.backButtonText}>← Back</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Filter buttons + Export */}
        <View style={styles.filterRow}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.filterButtonsScroll}
            contentContainerStyle={styles.filterButtonsContent}
          >
            {(['today', 'yesterday', 'this_week', 'archive'] as BusinessDateFilter[]).map((f) => (
              <TouchableOpacity
                key={f}
                style={[styles.filterButton, dateFilter === f && styles.filterButtonActive]}
                onPress={() => handleFilterSelect(f)}
                disabled={loadingFilter}
              >
                <Text style={[styles.filterButtonText, dateFilter === f && styles.filterButtonTextActive]}>
                  {FILTER_LABELS[f]}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <TouchableOpacity
            style={styles.exportButton}
            onPress={() => setShowExportDropdown(true)}
            disabled={loadingFilter}
          >
            <Text style={styles.exportButtonText}>Export ▼</Text>
          </TouchableOpacity>
        </View>

        {/* Export dropdown modal */}
        <Modal visible={showExportDropdown} transparent animationType="fade">
          <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowExportDropdown(false)}>
            <View style={styles.exportDropdownMenu}>
              <TouchableOpacity style={styles.exportDropdownItem} onPress={handleExportCurrent}>
                <Text style={styles.exportDropdownItemText}>Export current view (CSV)</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.exportDropdownItem} onPress={handleExportArchive}>
                <Text style={styles.exportDropdownItemText}>Export archive (CSV)</Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </Modal>

        {loadingFilter && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color="#667eea" />
          </View>
        )}

        {/* Appointments List */}
        <View style={styles.appointmentsContainer}>
          <View style={styles.healthCard}>
            <Text style={styles.healthTitle}>Queue Health</Text>
            <Text style={styles.healthText}>
              Total: {queueHealth.total} · In service: {queueHealth.inService} · Delayed: {queueHealth.delayed} · Avg ahead: {queueHealth.avgAhead}
            </Text>
          </View>
          <TouchableOpacity
            style={styles.addBookingBanner}
            onPress={handleCreateAppointment}
            activeOpacity={0.88}
            accessibilityRole="button"
            accessibilityLabel="Add booking"
          >
            <Text style={styles.addBookingBannerPlus}>+</Text>
            <Text style={styles.addBookingBannerText}>Add booking</Text>
          </TouchableOpacity>
          <Text style={styles.sectionTitle}>
            {FILTER_LABELS[dateFilter]} ({appointments.length})
          </Text>
          {appointments.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>No appointments found</Text>
              <Text style={styles.emptyStateSubtext}>
                Pull down to refresh or check back later
              </Text>
            </View>
          ) : (
            appointmentDateSections.map((section) => (
              <View key={section.dateKey} style={styles.dateSectionBlock}>
                <Text style={styles.dateSectionHeading}>{section.title}</Text>
                <Text style={styles.dateSectionCount}>
                  {section.items.length} booking{section.items.length === 1 ? '' : 's'}
                </Text>
                {section.items.map((appointment) => {
                  const expanded = expandedAppointmentId === appointment.id;
                  return (
                    <View key={appointment.id} style={styles.appointmentCard}>
                      <TouchableOpacity
                        style={styles.compactAppointmentRow}
                        activeOpacity={0.65}
                        onPress={() =>
                          setExpandedAppointmentId((cur) => (cur === appointment.id ? null : appointment.id))
                        }
                      >
                        <View style={styles.compactLeft}>
                          <Text style={styles.compactTime}>{appointment.appointmentTime || '—'}</Text>
                          <Text style={styles.compactService} numberOfLines={1}>
                            {appointment.customerName}
                          </Text>
                          <Text style={styles.compactBusiness} numberOfLines={1}>
                            {appointment.serviceType}
                          </Text>
                        </View>
                        <View style={styles.compactRight}>
                          <View style={styles.compactBadgesRow}>
                            <View style={[styles.statusBadge, { backgroundColor: getStatusColor(appointment.status) }]}>
                              <Text style={styles.statusText}>{getStatusText(appointment.status)}</Text>
                            </View>
                            <AppointmentPrepaidBadge appointment={appointment} variant="pill" />
                          </View>
                          <Text style={styles.compactTapHint}>{expanded ? 'Tap to hide' : 'Tap for details'}</Text>
                        </View>
                      </TouchableOpacity>

                      {expanded && (
                        <View style={styles.expandedAppointmentBlock}>
                          <AppointmentPrepaidBadge appointment={appointment} variant="banner" />

                          <View style={styles.appointmentDetailRow}>
                            <Image source={require('../assets/images/bookapt-logo.png')} style={styles.bookaptIconSmall} resizeMode="contain" />
                            <Text style={styles.appointmentDetails}>{appointment.appointmentDate} at {appointment.appointmentTime}</Text>
                          </View>
                          <Text style={styles.appointmentDetails}>📞 {appointment.phone}</Text>
                          <Text style={styles.appointmentDetails}>📧 {appointment.email}</Text>
                          <Text style={styles.appointmentDetails}>📍 {appointment.address}</Text>
                          <Text style={styles.appointmentDetails}>🔧 Service: {appointment.serviceType}</Text>
                          {customerQueueVisibility !== 'off' && getLiveQueueState(appointment) === 'show' && (
                            <LiveQueueCard
                              insight={queueInsights[appointment.id]}
                              onRefresh={onQueueRefresh}
                              isRefreshing={queueRefreshing}
                              lastUpdatedAt={lastQueueRefreshAt}
                            />
                          )}
                          {customerQueueVisibility !== 'off' && getLiveQueueState(appointment) === 'future' && (
                            <Text style={styles.appointmentDetails}>
                              Live Queue will be available on the appointment day.
                            </Text>
                          )}
                          {customerQueueVisibility !== 'off' ? (
                          <Text style={styles.appointmentDetails}>
                            🧭 Queue stage: {appointment.queueStage || 'booked'}
                            {(appointment.delayMinutes ?? 0) > 0 ? ` · Delay +${appointment.delayMinutes}m` : ''}
                          </Text>
                          ) : null}
                          {businessHours && (
                            <Text style={styles.appointmentDetails}>
                              🕐 Business Hours: {formatBusinessHours(businessHours)}
                            </Text>
                          )}
                          {appointment.specialInstructions &&
                            (appointment.specialInstructions.includes('Quantity:') ||
                              appointment.specialInstructions.includes('Selected Options:') ||
                              appointment.specialInstructions.includes('×')) && (
                            <View style={{ marginTop: 8, padding: 12, backgroundColor: '#F8F9FA', borderRadius: 8 }}>
                              <Text style={[styles.appointmentDetails, { fontWeight: 'bold', marginBottom: 6, fontSize: 14 }]}>
                                📦 Product Order Details:
                              </Text>
                              {(() => {
                                const productStrings = appointment.specialInstructions.split(' || ');
                                return (
                                  <>
                                    {productStrings.map((productString, productIdx) => {
                                      const parts = productString.split(' | ');
                                      let productName = appointment.serviceType;
                                      let quantity = '';
                                      let options = '';
                                      parts.forEach((part) => {
                                        if (part.includes('Quantity:')) {
                                          const match = part.match(/Quantity:\s*(\d+)\s*×\s*(.+)/);
                                          if (match) {
                                            quantity = match[1];
                                            productName = match[2].trim();
                                          } else {
                                            quantity = part.replace('Quantity:', '').trim();
                                          }
                                        } else if (part.includes('Selected Options:')) {
                                          options = part.replace('Selected Options:', '').trim();
                                        }
                                      });
                                      const optionsList = options ? options.split(',').map((opt) => opt.trim()).filter(Boolean) : [];
                                      return (
                                        <View key={productIdx} style={{ marginBottom: productIdx < productStrings.length - 1 ? 12 : 0 }}>
                                          {productStrings.length > 1 && (
                                            <Text style={[styles.appointmentDetails, { marginBottom: 4, fontWeight: 'bold', fontSize: 14 }]}>
                                              Product {productIdx + 1}:
                                            </Text>
                                          )}
                                          <Text style={[styles.appointmentDetails, { marginBottom: 4 }]}>🛍️ Product: {productName}</Text>
                                          {quantity && (
                                            <Text style={[styles.appointmentDetails, { marginBottom: 4 }]}>🔢 Quantity: {quantity}</Text>
                                          )}
                                          {optionsList.length > 0 && (
                                            <View style={{ marginBottom: 4 }}>
                                              <Text style={[styles.appointmentDetails, { marginBottom: 2, fontWeight: '600' }]}>
                                                ⚙️ Product Options:
                                              </Text>
                                              {optionsList.map((option, idx) => (
                                                <Text key={idx} style={[styles.appointmentDetails, { marginLeft: 12, marginBottom: 2 }]}>
                                                  • {option}
                                                </Text>
                                              ))}
                                            </View>
                                          )}
                                          {productIdx === 0 && (
                                            <View style={[styles.appointmentDetailRow, { marginTop: 4 }]}>
                                              <Image source={require('../assets/images/bookapt-logo.png')} style={styles.bookaptIconSmall} resizeMode="contain" />
                                              <Text style={[styles.appointmentDetails, { color: '#666' }]}>
                                                Date: {appointment.appointmentDate} at {appointment.appointmentTime}
                                              </Text>
                                            </View>
                                          )}
                                        </View>
                                      );
                                    })}
                                  </>
                                );
                              })()}
                            </View>
                          )}
                          {appointment.vehicleHandoff ? (
                            <Text style={styles.appointmentDetails}>
                              🅿️ Visit type:{' '}
                              {appointment.vehicleHandoff === 'drop_off'
                                ? 'Customer dropping off vehicle'
                                : 'Customer waiting on-site'}
                            </Text>
                          ) : null}
                          {appointment.vehicleYear && appointment.vehicleYear !== 'N/A' && (
                            <>
                              <Text style={styles.appointmentDetails}>
                                🚗 Vehicle: {appointment.vehicleYear} {appointment.vehicleMake} {appointment.vehicleModel}
                              </Text>
                              {appointment.vehicleLicensePlate ? (
                                <Text style={styles.appointmentDetails}>
                                  🔖 Plate: {appointment.vehicleLicensePlate}
                                </Text>
                              ) : null}
                              {appointment.vehicleMileage ? (
                                <Text style={styles.appointmentDetails}>📏 Mileage: {appointment.vehicleMileage} miles</Text>
                              ) : null}
                              <Text style={styles.appointmentDetails}>
                                🔢 VIN: {appointment.vehicleVin || 'Not provided'}
                                {appointment.vinMethod &&
                                  ` (${appointment.vinMethod === 'typed' ? 'Typed' : appointment.vinMethod === 'photo' ? 'Photo' : 'Skipped'})`}
                              </Text>
                              {appointment.vehicleType ? (
                                <Text style={styles.appointmentDetails}>🚙 Type: {appointment.vehicleType}</Text>
                              ) : null}
                            </>
                          )}
                          {appointment.carPhotos && appointment.carPhotos.length > 0 && (
                            <Text style={styles.appointmentDetails}>
                              📷 Car Photos: {appointment.photoCount || appointment.carPhotos.length} photo(s) captured
                            </Text>
                          )}
                          <Text style={styles.appointmentDetails}>📋 Service History: {appointment.serviceHistory}</Text>
                          {appointment.specialInstructions &&
                            !appointment.specialInstructions.includes('Quantity:') &&
                            !appointment.specialInstructions.includes('Selected Options:') &&
                            !appointment.specialInstructions.includes('×') && (
                              <Text style={styles.appointmentDetails}>📝 Instructions: {appointment.specialInstructions}</Text>
                            )}
                          {appointment.workerName && (
                            <Text style={styles.appointmentDetails}>👤 Worker: {appointment.workerName}</Text>
                          )}
                          {appointment.actualDuration && (
                            <Text style={styles.appointmentDetails}>⏱️ Duration: {appointment.actualDuration} minutes</Text>
                          )}
                          {appointment.paymentData && !isBookingPrepaid(appointment) && (
                            <Text style={styles.appointmentDetails}>
                              💳 Payment: ${Number(appointment.paymentData.amount ?? 0).toFixed(2)} (
                              {formatBookingPaymentMethod(appointment.paymentData.paymentMethod)})
                            </Text>
                          )}

                          <View style={styles.appointmentActions}>
                            {appointment.status === 'confirmed' && (
                              <>
                                <TouchableOpacity
                                  style={[styles.actionButton, styles.queueActionButton]}
                                  onPress={() => handleQueueAction(appointment, 'checked_in')}
                                >
                                  <Text style={styles.actionButtonText}>Check-in</Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                  style={[styles.actionButton, styles.queueActionButton]}
                                  onPress={() => handleQueueAction(appointment, 'in_service')}
                                >
                                  <Text style={styles.actionButtonText}>Start</Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                  style={[styles.actionButton, styles.queueActionButton]}
                                  onPress={() => handleQueueAction(appointment, 'delay')}
                                >
                                  <Text style={styles.actionButtonText}>+10m Delay</Text>
                                </TouchableOpacity>
                              </>
                            )}
                            <TouchableOpacity style={styles.actionButton} onPress={() => handleEditAppointment(appointment)}>
                              <Text style={styles.actionButtonText}>Edit</Text>
                            </TouchableOpacity>
                            {appointment.orderLineItems && appointment.orderLineItems.length > 0 && (
                              <TouchableOpacity style={styles.actionButton} onPress={() => handleEditOrderItems(appointment)}>
                                <Text style={styles.actionButtonText}>Edit Order</Text>
                              </TouchableOpacity>
                            )}
                            {appointment.status === 'confirmed' && (
                              <TouchableOpacity style={styles.actionButton} onPress={() => handleTrackDuration(appointment)}>
                                <Text style={styles.actionButtonText}>Track Duration</Text>
                              </TouchableOpacity>
                            )}
                            {appointment.status === 'completed' && (
                              <TouchableOpacity style={styles.actionButton} onPress={() => handleRequestPayment(appointment)}>
                                <Text style={styles.actionButtonText}>Request Payment</Text>
                              </TouchableOpacity>
                            )}
                            {(appointment.status === 'confirmed' || appointment.status === 'completed') &&
                              !isBookingPrepaid(appointment) && (
                                <TouchableOpacity
                                  style={[styles.actionButton, styles.paymentButton]}
                                  onPress={() => handleProcessPayment(appointment)}
                                >
                                  <Text style={styles.actionButtonText}>💳 Process Payment</Text>
                                </TouchableOpacity>
                              )}
                            {appointment.status === 'confirmed' && (
                              <TouchableOpacity
                                style={[styles.actionButton, styles.queueDoneButton]}
                                onPress={() => handleQueueAction(appointment, 'complete')}
                              >
                                <Text style={styles.actionButtonText}>Mark Complete</Text>
                              </TouchableOpacity>
                            )}
                            {appointment.status === 'confirmed' && (
                              <TouchableOpacity
                                style={[styles.actionButton, styles.noShowButton]}
                                onPress={() => handleQueueAction(appointment, 'no_show')}
                              >
                                <Text style={styles.actionButtonText}>No-show</Text>
                              </TouchableOpacity>
                            )}
                            {appointment.status === 'confirmed' && (
                              <TouchableOpacity
                                style={[styles.actionButton, { backgroundColor: '#dc3545' }]}
                                onPress={() => {
                                  console.log('🔄 Cancel button clicked for appointment:', appointment.id);
                                  handleCancelAppointment(appointment);
                                }}
                                activeOpacity={0.7}
                              >
                                <Text style={styles.actionButtonText}>Cancel</Text>
                              </TouchableOpacity>
                            )}
                            {appointment.status === 'confirmed' && (
                              <TouchableOpacity
                                style={[styles.actionButton, styles.deleteButton]}
                                onPress={() => handleDeleteAppointment(appointment)}
                                activeOpacity={0.7}
                              >
                                <Text style={styles.actionButtonText}>Delete</Text>
                              </TouchableOpacity>
                            )}
                          </View>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            ))
          )}
        </View>

      </ScrollView>

      {/* Bottom Menu */}
      {onTabPress && (
        <BusinessOwnerBottomMenu
          activeTab="appointments"
          onTabPress={onTabPress}
          onNavigateToSettings={onNavigateToSettings}
          onLogout={onLogout}
        />
      )}

      {/* Modals */}
      <EditAppointmentModal
        visible={showEditAppointment}
        onClose={() => {
          setShowEditAppointment(false);
          setSelectedAppointment(null);
        }}
        booking={selectedAppointment}
        onSave={handleSaveAppointment}
      />

      <EditOrderItemsModal
        visible={showEditOrderItems}
        appointment={selectedAppointment}
        onClose={() => {
          setShowEditOrderItems(false);
          setSelectedAppointment(null);
        }}
        onSaved={() => {
          loadAppointmentsByFilter(dateFilter, { showLoading: false });
        }}
      />

      <DurationTrackingModal
        visible={showDurationTracking}
        onClose={() => {
          setShowDurationTracking(false);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment as any}
        onSave={handleSaveDuration}
      />

      <RequestPaymentModal
        visible={showRequestPayment}
        appointment={selectedAppointment}
        onClose={() => {
          setShowRequestPayment(false);
          setSelectedAppointment(null);
        }}
        onSent={() => {
          loadAppointmentsByFilter(dateFilter, { showLoading: false });
        }}
        onNavigateToPackageSelection={onNavigateToPackageSelection}
      />

      <AppointmentPaymentModal
        visible={showPayment}
        onClose={() => {
          setShowPayment(false);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment}
        onPaymentSuccess={handlePaymentSuccess}
      />

      {/* Create Appointment Modal */}
      <CreateAppointmentModal
        visible={showCreateAppointment}
        onClose={() => setShowCreateAppointment(false)}
        workers={workers}
        onSave={handleSaveNewAppointment}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 100, // Space for bottom menu
  },
  header: {
    backgroundColor: '#007bff',
    padding: 20,
    paddingTop: 50,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  headerBookaptIcon: {
    width: 28,
    height: 28,
    marginRight: 10,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
  },
  appointmentDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  bookaptIconSmall: {
    width: 18,
    height: 18,
    marginRight: 8,
  },
  headerButtons: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  refreshButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 5,
    marginRight: 8,
  },
  refreshButtonText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  backButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 5,
  },
  backButtonText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  filterRow: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 10,
    gap: 10,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  filterButtonsScroll: {
    flex: 1,
  },
  filterButtonsContent: {
    flexDirection: 'row',
    gap: 8,
  },
  filterButton: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: '#f0f0f0',
    borderRadius: 8,
    justifyContent: 'center',
  },
  filterButtonActive: {
    backgroundColor: '#667eea',
  },
  filterButtonText: {
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
  filterButtonTextActive: {
    color: '#fff',
    fontWeight: '600',
  },
  exportButton: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: '#667eea',
    borderRadius: 8,
    justifyContent: 'center',
  },
  exportButtonText: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-start',
    paddingTop: 100,
    paddingHorizontal: 20,
  },
  exportDropdownMenu: {
    backgroundColor: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
    minWidth: 240,
  },
  exportDropdownItem: {
    paddingVertical: 14,
    paddingHorizontal: 18,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#eee',
  },
  exportDropdownItemText: {
    fontSize: 16,
    color: '#333',
  },
  loadingOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  appointmentsContainer: {
    padding: 20,
  },
  healthCard: {
    backgroundColor: '#EEF6FF',
    borderWidth: 1,
    borderColor: '#CFE5FF',
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
  },
  healthTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#0B4A8B',
    marginBottom: 4,
  },
  healthText: {
    fontSize: 13,
    color: '#0B4A8B',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 15,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyStateText: {
    fontSize: 16,
    color: '#6c757d',
    marginBottom: 5,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#adb5bd',
  },
  appointmentCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  dateSectionBlock: {
    marginBottom: 8,
  },
  dateSectionHeading: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1C1C1E',
    marginTop: 4,
    marginBottom: 2,
  },
  dateSectionCount: {
    fontSize: 12,
    color: '#8E8E93',
    marginBottom: 10,
  },
  compactAppointmentRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },
  compactLeft: {
    flex: 1,
    minWidth: 0,
  },
  compactTime: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  compactService: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  compactBusiness: {
    fontSize: 13,
    color: '#8E8E93',
  },
  compactRight: {
    alignItems: 'flex-end',
    maxWidth: '46%',
  },
  compactBadgesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 6,
    alignItems: 'center',
  },
  compactTapHint: {
    marginTop: 6,
    fontSize: 11,
    color: '#8E8E93',
  },
  expandedAppointmentBlock: {
    marginTop: 14,
    paddingTop: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#E5E5EA',
  },
  appointmentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  customerName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  appointmentDetails: {
    fontSize: 14,
    color: '#6c757d',
    marginBottom: 4,
    lineHeight: 20,
  },
  appointmentActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 12,
    gap: 8,
  },
  actionButton: {
    backgroundColor: '#007bff',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  actionButtonText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  paymentButton: {
    backgroundColor: '#28a745',
  },
  queueActionButton: {
    backgroundColor: '#0B7CFF',
  },
  queueDoneButton: {
    backgroundColor: '#16A34A',
  },
  noShowButton: {
    backgroundColor: '#B42318',
  },
  deleteButton: {
    backgroundColor: '#842029',
  },
  addBookingBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#007bff',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
    marginBottom: 14,
  },
  addBookingBannerPlus: {
    fontSize: 22,
    color: '#fff',
    fontWeight: '700',
    marginTop: -2,
  },
  addBookingBannerText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default AppointmentsScreen;
